import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { trackEvent } from "@/lib/tracker";
import { FadeUp, Overline } from "@/components/Reveal";

const GREETING =
  "Describe your requirement in your own words — for example: “We need around 200 high-memory GPUs for a data centre in Dubai.” I will structure it into a commercial requirement for your review.";

const SUMMARY_RE = /<requirement_summary>([\s\S]*?)<\/requirement_summary>/;

const stripSummary = (text) => text.replace(SUMMARY_RE, "").trim();

const parseSummary = (text) => {
  const m = text.match(SUMMARY_RE);
  if (!m) return null;
  try {
    return JSON.parse(m[1]);
  } catch {
    return null;
  }
};

export const RequirementBuilder = () => {
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const sessionRef = useRef(crypto.randomUUID());
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    if (messages.length === 1) trackEvent("ai_started");
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let full = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const data = JSON.parse(part.slice(6));
          if (data.delta) {
            full += data.delta;
            setMessages((m) => {
              const next = [...m];
              next[next.length - 1] = { role: "assistant", content: stripSummary(full) };
              return next;
            });
          }
          if (data.error) toast.error(data.error);
        }
      }
      const s = parseSummary(full);
      if (s) {
        setSummary(s);
        trackEvent("ai_summary_generated");
      }
    } catch {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = {
          role: "assistant",
          content: "The assistant is temporarily unavailable. You can submit your requirement directly via the requirement form.",
        };
        return next;
      });
    }
    setBusy(false);
  };

  const summaryQuery = summary
    ? `/request?${new URLSearchParams({
        source: "builder",
        category: summary.product_class || "",
        product: summary.solution || "",
        quantity: summary.quantity || "",
        destination: summary.destination || "",
        notes: `Requirement Builder summary — Memory: ${summary.memory || "n/a"}; Configuration: ${summary.configuration || "n/a"}; Delivery: ${summary.delivery || "n/a"}; Incoterm: ${summary.incoterm || "n/a"}. ${summary.notes || ""}`,
      }).toString()}`
    : "/request?source=builder";

  return (
    <section id="builder" className="relative py-16 lg:py-24 bg-[#FAFAFA] border-y border-midnight/10" data-testid="builder-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-start">
        <FadeUp>
          <Overline>Royal AI — Requirement Builder</Overline>
          <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-midnight">
            What are you building?
          </h2>
          <p className="text-midnight/55 text-lg mt-6 leading-relaxed max-w-lg">
            Describe your deployment in your own words. Our assistant structures it into a commercially useful requirement — application, quantity, memory class, configuration, destination and delivery — for your review before submission.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {["AI Data Centre", "GPU Cluster", "Workstation Fleet", "Cloud Infrastructure"].map((q) => (
              <button
                key={q}
                onClick={() => send(`We are building: ${q}`)}
                disabled={busy}
                data-testid={`builder-quick-${q.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-xs font-bold tracking-[0.1em] text-midnight/70 bg-white hairline px-4 py-2.5 hover:border-gold hover:text-gold transition-colors disabled:opacity-40"
              >
                {q.toUpperCase()}
              </button>
            ))}
          </div>
          <p className="mt-10 text-sm text-midnight/50 max-w-md">
            Already know the exact manufacturer part number?{" "}
            <Link to="/request" data-testid="builder-direct-rfq" className="font-medium text-gold hover:text-midnight transition-colors">
              Submit it directly →
            </Link>
          </p>
          <p className="mt-4 text-xs text-midnight/40 max-w-md leading-relaxed">
            The assistant never quotes stock, pricing or lead times. All availability is subject to commercial confirmation.
          </p>
        </FadeUp>

        <FadeUp delay={0.15}>
          <div className="bg-white hairline shadow-medium" data-testid="builder-panel">
            <div className="flex items-center gap-3 px-6 py-4 border-b border-midnight/10">
              <span className="w-2 h-2 rounded-full bg-gold" />
              <span className="font-heading text-sm font-medium text-midnight">Requirement Assistant</span>
              <span className="ml-auto text-xs text-midnight/40">Royal Deals FZ-LLC</span>
            </div>

            <div ref={scrollRef} className="h-[400px] overflow-y-auto px-6 py-6 space-y-4 bg-white" data-testid="builder-messages">
              {messages.map((m, i) => (
                <div key={i} className={m.role === "user" ? "text-right" : ""}>
                  <div
                    className={`inline-block max-w-[90%] text-left px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-midnight text-white"
                        : "bg-[#F3F4F6] text-midnight/80"
                    }`}
                  >
                    {m.content || (busy && i === messages.length - 1 ? <Loader2 size={14} className="animate-spin text-gold" /> : "")}
                  </div>
                </div>
              ))}

              {summary && (
                <div className="border border-gold/40 bg-gold/[0.05] p-6" data-testid="builder-summary">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-4">Requirement Summary</p>
                  <dl className="space-y-2 text-sm">
                    {Object.entries(summary).filter(([, v]) => v).map(([k, v]) => (
                      <div key={k} className="flex gap-3">
                        <dt className="text-xs font-bold uppercase tracking-wide text-midnight/40 w-28 shrink-0 pt-0.5">{k.replace(/_/g, " ")}</dt>
                        <dd className="text-midnight/80">{v}</dd>
                      </div>
                    ))}
                    <div className="flex gap-3">
                      <dt className="text-xs font-bold uppercase tracking-wide text-midnight/40 w-28 shrink-0 pt-0.5">Status</dt>
                      <dd className="text-midnight font-medium">Requesting availability — subject to commercial confirmation</dd>
                    </div>
                  </dl>
                  <Link
                    to={summaryQuery}
                    data-testid="builder-submit-summary"
                    className="mt-5 inline-block bg-midnight text-white text-sm font-medium px-6 py-3 hover:bg-midnight-light transition-colors"
                  >
                    Submit to Royal Deals FZ-LLC
                  </Link>
                </div>
              )}
            </div>

            <div className="border-t border-midnight/10 p-4 flex gap-3 bg-white">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Describe your compute requirement…"
                data-testid="builder-input"
                className="flex-1 bg-[#F9FAFB] border border-midnight/10 px-4 py-3 text-sm text-midnight placeholder:text-midnight/35 outline-none focus:border-gold/60 transition-colors"
              />
              <button
                onClick={() => send()}
                disabled={busy || !input.trim()}
                data-testid="builder-send"
                className="bg-midnight text-white px-4 py-3 hover:bg-midnight-light transition-colors disabled:opacity-40"
                aria-label="Send message"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};
